// New-project wizard.
//
// 3 steps:
//   1. Name + code + due date  — creates the project.
//   2. Episode 1 metadata + 4 file uploads (presigned PUT to R2).
//   3. Validation report + auto-strategy override + Process now.
//
// State is local to this component; once `process` returns we navigate into
// the project's setup page where the user watches the pipeline finish.

import type { ImportStrategy } from '@ohear/shared'
import { Button, Card, Input, Select, Tab, TabList, TabPanel, Tabs } from '@ohear/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import {
  ProcessingStatus,
  StrategyPicker,
  UploadZone,
  ValidationReport,
  useEpisodeUploads,
} from '../../features/project-setup'
import { useCreateEpisode, useEpisode, useProcessEpisode } from '../../lib/api/episodes'
import { isValidProjectCode, useCreateProject } from '../../lib/api/producer-projects'
import { useRequireRole } from '../../lib/auth'

export const Route = createFileRoute('/producer/projects/new')({
  component: NewProjectPage,
})

type Step = '1-basic' | '2-episode' | '3-validate'

interface BasicState {
  name: string
  code: string
  due_date: string
  /** Server-issued project id once Step 1 completes. */
  project_id: string | null
}

interface EpisodeState {
  title: string
  /** Selected from a small fixed list; the value is the nominal fps. */
  frame_rate: '23.976' | '24' | '25' | '29.97' | '30' | '50' | '59.94' | '60'
  drop_frame: boolean
  /** Server-issued episode id once "Save metadata" completes. */
  episode_id: string | null
}

const FRAME_RATE_OPTIONS: EpisodeState['frame_rate'][] = [
  '23.976',
  '24',
  '25',
  '29.97',
  '30',
  '50',
  '59.94',
  '60',
]

function NewProjectPage() {
  useRequireRole(['owner', 'member'])
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('1-basic')

  const [basic, setBasic] = useState<BasicState>({
    name: '',
    code: '',
    due_date: '',
    project_id: null,
  })
  const [episode, setEpisode] = useState<EpisodeState>({
    title: 'Episode 1',
    frame_rate: '23.976',
    drop_frame: false,
    episode_id: null,
  })
  const [strategy, setStrategy] = useState<ImportStrategy | null>(null)

  const createProject = useCreateProject()
  const createEpisode = useCreateEpisode()
  const processEpisode = useProcessEpisode()

  const uploads = useEpisodeUploads(episode.episode_id)
  const episodeQ = useEpisode(episode.episode_id ?? undefined)
  const validation = episodeQ.data?.validation ?? null

  // ---- Step 1 ----
  const codeValid = basic.code === '' || isValidProjectCode(basic.code)
  const canStep1Continue =
    basic.name.trim().length > 0 && isValidProjectCode(basic.code) && !createProject.isPending

  const onStep1Submit = async () => {
    const created = await createProject.mutateAsync({
      name: basic.name.trim(),
      code: basic.code,
      due_date: basic.due_date || null,
    })
    setBasic((b) => ({ ...b, project_id: created.id }))
    setStep('2-episode')
  }

  // ---- Step 2 ----
  const canCreateEpisode =
    !episode.episode_id && episode.title.trim().length > 0 && basic.project_id != null
  const onCreateEpisode = async () => {
    if (!basic.project_id) return
    const created = await createEpisode.mutateAsync({
      project_id: basic.project_id,
      episode_number: 1,
      title: episode.title.trim(),
      frame_rate_fps: Number.parseFloat(episode.frame_rate),
      drop_frame: episode.drop_frame,
    })
    setEpisode((e) => ({ ...e, episode_id: created.id }))
  }

  const canStep2Continue = uploads.requiredDone

  // ---- Step 3 ----
  const recommended = validation?.recommended_strategy ?? 'A'
  const effectiveStrategy = strategy ?? recommended

  const onProcess = async () => {
    if (!episode.episode_id) return
    await processEpisode.mutateAsync(episode.episode_id)
    if (basic.project_id) {
      void navigate({
        to: '/producer/projects/$projectId/setup' as '/producer/projects/$projectId',
        params: { projectId: basic.project_id },
      })
    }
  }

  return (
    <section>
      <header className="mb-5 pb-4 border-b border-[var(--border)] flex flex-col gap-1">
        <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">Wizard</div>
        <h1 className="text-[22px] font-semibold tracking-tight text-[var(--text)] m-0">
          New project
        </h1>
        <p className="text-[13px] text-[var(--text-2)] m-0">
          Three steps: project basics, episode upload, validation. You can come back later.
        </p>
      </header>

      <Tabs defaultValue="1-basic" value={step} onValueChange={(v) => setStep(v as Step)}>
        <TabList aria-label="New-project wizard step">
          <Tab value="1-basic">1. Basics</Tab>
          <Tab value="2-episode" disabled={!basic.project_id}>
            2. Episode upload
          </Tab>
          <Tab value="3-validate" disabled={!validation}>
            3. Validation
          </Tab>
        </TabList>

        <TabPanel value="1-basic">
          <Card title="Project basics">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Project name" htmlFor="np-name">
                <Input
                  id="np-name"
                  value={basic.name}
                  onChange={(e) => setBasic((b) => ({ ...b, name: e.target.value }))}
                  placeholder="Acme: Season 2"
                  autoComplete="off"
                />
              </FormField>
              <FormField
                label="Project code"
                htmlFor="np-code"
                hint="3-6 letters/digits, uppercase. Used in delivery filenames."
              >
                <Input
                  id="np-code"
                  value={basic.code}
                  onChange={(e) => setBasic((b) => ({ ...b, code: e.target.value.toUpperCase() }))}
                  placeholder="ACME2"
                  invalid={!codeValid}
                  aria-invalid={!codeValid}
                  maxLength={6}
                  autoComplete="off"
                />
                {!codeValid ? (
                  <p className="mt-1 text-[12px] text-[var(--amber)]">
                    Project code must be 3-6 alphanumeric uppercase characters.
                  </p>
                ) : null}
              </FormField>
              <FormField label="Due date" htmlFor="np-due">
                <Input
                  id="np-due"
                  type="date"
                  value={basic.due_date}
                  onChange={(e) => setBasic((b) => ({ ...b, due_date: e.target.value }))}
                />
              </FormField>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                onClick={onStep1Submit}
                loading={createProject.isPending}
                disabled={!canStep1Continue}
              >
                Continue
              </Button>
            </div>
            {createProject.isError ? (
              <p className="mt-2 text-[12px] text-[var(--red)]">
                {(createProject.error as Error).message}
              </p>
            ) : null}
          </Card>
        </TabPanel>

        <TabPanel value="2-episode">
          <Card title="Episode 1 metadata">
            <div className="grid gap-4 sm:grid-cols-3">
              <FormField label="Episode title" htmlFor="np-ep-title">
                <Input
                  id="np-ep-title"
                  value={episode.title}
                  onChange={(e) => setEpisode((s) => ({ ...s, title: e.target.value }))}
                  disabled={Boolean(episode.episode_id)}
                />
              </FormField>
              <FormField label="Frame rate" htmlFor="np-ep-fr">
                <Select
                  id="np-ep-fr"
                  value={episode.frame_rate}
                  onChange={(e) =>
                    setEpisode((s) => ({
                      ...s,
                      frame_rate: e.target.value as EpisodeState['frame_rate'],
                    }))
                  }
                  disabled={Boolean(episode.episode_id)}
                >
                  {FRAME_RATE_OPTIONS.map((fr) => (
                    <option key={fr} value={fr}>
                      {fr} fps
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField label="Drop-frame" htmlFor="np-ep-df">
                <Select
                  id="np-ep-df"
                  value={episode.drop_frame ? 'yes' : 'no'}
                  onChange={(e) =>
                    setEpisode((s) => ({ ...s, drop_frame: e.target.value === 'yes' }))
                  }
                  disabled={Boolean(episode.episode_id)}
                >
                  <option value="no">No (NDF)</option>
                  <option value="yes">Yes (DF)</option>
                </Select>
              </FormField>
            </div>
            {!episode.episode_id ? (
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={onCreateEpisode}
                  loading={createEpisode.isPending}
                  disabled={!canCreateEpisode}
                >
                  Save metadata + start upload
                </Button>
              </div>
            ) : null}
          </Card>

          {episode.episode_id ? (
            <Card title="Upload reference files" className="mt-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <UploadZone
                  label="Reference video"
                  required
                  accept="video/*,.mov,.mp4,.mxf"
                  maxBytes={20_000_000_000}
                  hint="The cut the actors will record against. MOV / MP4 / MXF, up to ~20 GB."
                  done={uploads.states.video.status === 'done'}
                  progress={
                    uploads.states.video.status === 'uploading'
                      ? uploads.states.video.progress
                      : null
                  }
                  error={uploads.states.video.error}
                  file={uploads.states.video.file}
                  onFileSelected={(f) => void uploads.start('video', f)}
                />
                <UploadZone
                  label="Reference audio"
                  required
                  accept="audio/*,.wav,.flac"
                  maxBytes={5_000_000_000}
                  hint="48 kHz / 24-bit recommended. Used for prediction + alignment."
                  done={uploads.states.ref_audio.status === 'done'}
                  progress={
                    uploads.states.ref_audio.status === 'uploading'
                      ? uploads.states.ref_audio.progress
                      : null
                  }
                  error={uploads.states.ref_audio.error}
                  file={uploads.states.ref_audio.file}
                  onFileSelected={(f) => void uploads.start('ref_audio', f)}
                />
                <UploadZone
                  label="Script (optional)"
                  accept=".pdf,.docx,.txt,.fdx"
                  maxBytes={50_000_000}
                  hint="Final-Draft, Word, plain text or PDF. Improves alignment accuracy."
                  done={uploads.states.script.status === 'done'}
                  progress={
                    uploads.states.script.status === 'uploading'
                      ? uploads.states.script.progress
                      : null
                  }
                  error={uploads.states.script.error}
                  file={uploads.states.script.file}
                  onFileSelected={(f) => void uploads.start('script', f)}
                />
                <UploadZone
                  label="SRT subtitles (optional)"
                  accept=".srt"
                  maxBytes={50_000_000}
                  hint="Subtitle timecodes pre-align cues; speeds up Strategy A and C."
                  done={uploads.states.srt.status === 'done'}
                  progress={
                    uploads.states.srt.status === 'uploading' ? uploads.states.srt.progress : null
                  }
                  error={uploads.states.srt.error}
                  file={uploads.states.srt.file}
                  onFileSelected={(f) => void uploads.start('srt', f)}
                />
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setStep('1-basic')}>
                  Back
                </Button>
                <Button onClick={() => setStep('3-validate')} disabled={!canStep2Continue}>
                  Continue
                </Button>
              </div>
              {!canStep2Continue ? (
                <p className="mt-2 text-[12px] text-[var(--text-2)]">
                  Reference video + reference audio are required to continue.
                </p>
              ) : null}
            </Card>
          ) : null}
        </TabPanel>

        <TabPanel value="3-validate">
          {validation ? (
            <div className="space-y-4">
              <ValidationReport report={validation} />
              <Card>
                <StrategyPicker
                  recommended={recommended}
                  value={effectiveStrategy}
                  onChange={(s) => setStrategy(s)}
                  disabled={processEpisode.isPending}
                />
              </Card>
              {episodeQ.data ? (
                <ProcessingStatus
                  state={episodeQ.data.processing_state}
                  percent={episodeQ.data.processing_percent}
                />
              ) : null}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setStep('2-episode')}>
                  Back
                </Button>
                <Button
                  onClick={onProcess}
                  loading={processEpisode.isPending}
                  disabled={!episode.episode_id}
                >
                  Process now
                </Button>
              </div>
            </div>
          ) : (
            <Card>
              <p className="m-0 text-[14px] text-[var(--text-2)]">
                Validation report appears once the server has finished reading the uploaded files.
              </p>
            </Card>
          )}
        </TabPanel>
      </Tabs>
    </section>
  )
}

// Small inline helper, not worth a feature folder.
function FormField({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string
  htmlFor: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1 block text-[13px] font-medium text-[var(--text)]">
        {label}
      </label>
      {children}
      {hint ? <p className="mt-1 text-[12px] text-[var(--text-2)]">{hint}</p> : null}
    </div>
  )
}

// Suppress unused-import warning for `useMemo` if eslint complains; keep
// available for follow-up wiring of total-budget preview.
void useMemo
